import { formatMoney } from '@shopify/theme-currency'
import { useAccountLayoutContext } from './AccountProfileContext'
import { accountTranslations } from '../helpers/translations'

const CardTitle = ({ title, subTitle }) => {
  return (
    <div className={'flex flex-col'}>
      <h1 className={'h1 font-bold'}>{title}</h1>
      <h4 className="font-bold h4">{subTitle}</h4>
    </div>
  )
}

const CardSeparator = () => <div className={'my-8 border border-solid border-neutrals-secondary-grey opacity-50'}></div>

/**
 *
 * @param {{buttons: import('../types/page').CardButtons}} props
 */
const CardButtons = ({ buttons }) => {
  return (
    <>
      {buttons.map((button, index) => {
        return <div></div>
      })}
    </>
  )
}
/**
 *
 * @param {import('../types/page').CardProps} props
 * @returns
 */
const Cart = (props) => {
  return (
    <div className={'w-full -rotate-1 bg-secondary-violet/50'}>
      <div className={'flex h-full flex-col p-10 pb-8'}>
        <CardTitle title={props.content.title} subTitle={props.content.subtitle} />
        <CardSeparator />
      </div>
    </div>
  )
}

export const AccountAvailableBalance = ({ balance: balanceRaw, daysUntilRewardExpiryDate }) => {
  const { handlePushState, config } = useAccountLayoutContext()
  const balance = formatMoney((balanceRaw ?? 0) * 100)

  const hasRewards = !isNaN(daysUntilRewardExpiryDate) && daysUntilRewardExpiryDate > 0

  return (
    <div className={'flex w-full -rotate-1 flex-col justify-between bg-secondary-violet bg-opacity-[0.24]'}>
      <div className={'flex flex-1 flex-col p-10 pb-8'}>
        <CardTitle title={`${balance}`} subTitle={accountTranslations.dashboard.available_rewards.title} />
        <div className={'my-6 border-b border-neutrals-medium-grey'}></div>
        <div className={'space-y-4'}>
          <a href="/collections/shop-all" className={'btn btn-solid-black btn-md w-full'}>
            {accountTranslations.dashboard.available_rewards.shop_cta}
          </a>
          <button
            onClick={() => {
              if (config.path === 'fit-club') {
                document.getElementById('rewards-history')?.scrollIntoView({ behavior: 'smooth' })
              } else {
                handlePushState('fit-club')
              }
            }}
            className={'btn btn-outline-black btn-md w-full'}
            disabled={!hasRewards}
          >
            {accountTranslations.dashboard.available_rewards.view_rewards_cta}
          </button>
        </div>
      </div>
      <div className={'w-full bg-secondary-violet text-center'}>
        <div className={'paragraph-sm px-4 py-2'}>
          {hasRewards ? (
            <>
              Some of your rewards are expiring in <b>{daysUntilRewardExpiryDate} days</b>
            </>
          ) : (
            <>You have no rewards to claim</>
          )}
        </div>
      </div>
    </div>
  )
}

export const RewardsToClaim = ({ unexpiredTransactionItems, unassignedTransactionItems, handleToggle }) => {
  const hasUnexpiredItems = unexpiredTransactionItems.items.length > 0
  const hasUnassignedItems = unassignedTransactionItems.items.length > 0

  // Only render if we have either unexpired or unassigned items
  if (!hasUnexpiredItems && !hasUnassignedItems) {
    return null
  }

  const unexpiredTransactionItemsTotal = formatMoney((unexpiredTransactionItems.items.length ?? 0) * 100 * 2)
  const unexpiredShoeCount = unexpiredTransactionItems.items.length
  const unassignedShoeCount = unassignedTransactionItems.items.length

  // Determine the scenario and appropriate content
  let title, subtitle, description, buttonText, footerText, descriptiveCase

  if (hasUnexpiredItems && hasUnassignedItems && unexpiredShoeCount === unassignedShoeCount) {
    // Scenario 3: Both unexpired and expired shoes
    title = `${unexpiredTransactionItemsTotal}`
    descriptiveCase = 'Only new shoes to assign'
    subtitle = 'Rewards waiting to be claimed'
    description = `You have ${unexpiredShoeCount} pair${unexpiredShoeCount === 1 ? '' : 's'} of shoes waiting to be assigned and give you $2 a pair.`
    buttonText = 'Claim rewards'
    footerText = <>You have <b>{unexpiredTransactionItems.daysUntilUnexpiredExpiryDate}</b> more days to claim these rewards</>
  } else if (hasUnexpiredItems && unexpiredShoeCount !== unassignedShoeCount) {
    // Scenario 2: Only unexpired shoes (with rewards)
    title = `${unexpiredTransactionItemsTotal}`
    descriptiveCase = 'Old orders with shoes to assign'
    subtitle = 'Rewards waiting to be claimed'
    description = `You have ${unassignedShoeCount} pair${unexpiredShoeCount === 1 ? '' : 's'} of shoes waiting to be assigned. If they were purchased in the last 30 days, you'll receive $2 rewards for each pair assigned.`
    buttonText = 'Claim rewards'
    footerText = <>You have <b>{unexpiredTransactionItems.daysUntilUnexpiredExpiryDate}</b> more days to claim these rewards</>
  } else {
    // Scenario 1: Only expired shoes (no rewards)
    title = ''
    subtitle = `${unassignedShoeCount} pair${unassignedShoeCount === 1 ? '' : 's'} of shoes waiting to be assigned`
    description = 'These shoes were purchased more than 30 days ago and so won\'t receive rewards, however you can still assign them to your kids profiles'
    buttonText = 'Assign shoes'
    footerText = 'Shoes can still be assigned!'
  }

  return (
    <>
      <div className={'flex h-full w-full rotate-1 flex-col justify-between bg-secondary-paleblue bg-opacity-[0.24]'}>
        <div className={'flex flex-1 flex-col p-10 pb-8'}>
          <div className={'flex flex-col'}>
            <h1 className={'h1 font-bold'}>{title}</h1>
            <h4 className="font-bold h4">{subtitle}</h4>
          </div>
          <div className={'my-6 border-b border-neutrals-medium-grey'}></div>
          <div className={'space-y-6'}>
            <div className={'paragraph-md'}>
              {description}
            </div>
            <div onClick={handleToggle} className={'btn btn-solid-black btn-md w-full'}>
              {buttonText}
            </div>
          </div>
        </div>
        <div className={'w-full bg-secondary-paleblue text-center'}>
          <div className={'paragraph-sm px-4 py-2'}>
            {footerText}
          </div>
        </div>
      </div>
    </>
  )
}
